import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components/macro'
import { themeGet } from '@styled-system/theme-get'
import palette from 'src/designSystem/variables/palette'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import { useNavigateContext } from '../../Navigation/NavigateContext'
import {
  FilterCountChip,
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from './FilterBar.styles'
import {
  DropdownMenuItemStyle,
  DropdownMenuStyle,
} from './FilterDropdowns/FilterDropdown.styles'
import {
  FILTER_MENU_MAX_WIDTH,
  FILTER_MENU_MIN_WIDTH,
  FilterMenuSearchContext,
  filterHeadingFont,
} from './FilterDropdowns/FilterAccordion'

// Each filter opens below its row (accordion), not in a menu to the side.
const MoreFiltersMenu = styled.div`
  ${DropdownMenuStyle}
  position: absolute;
  display: flex;
  flex-direction: column;
  min-width: ${FILTER_MENU_MIN_WIDTH}px;
  max-width: ${FILTER_MENU_MAX_WIDTH}px;
  max-height: 70vh;
  overflow-y: auto;

  .filter-item {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    margin: 4px;

    .dropdown {
      padding: 2px 0;

      .dropdown-toggle {
        ${DropdownMenuItemStyle}

        /* Section names look like the headings over grouped options. */
        p {
          ${filterHeadingFont}
        }

        &[aria-expanded='true'],
        &[aria-expanded='true']:hover {
          background-color: ${themeGet('colors.bg.neutral')};
          color: ${palette.neutral['900']};
          svg path {
            fill: ${palette.neutral['500']};
          }
        }
      }

      &.show > .dropdown-menu {
        position: static !important;
        inset: auto !important;
        transform: none !important;
        margin: 2px 0 4px 12px !important;
        padding: 0;
        border: none;
        box-shadow: none;
        min-width: 0;
        width: auto;
      }
    }
  }
`

const MoreFiltersTopMenu = styled(Dropdown)`
  &.show {
    > .dropdown-menu {
      visibility: visible;
    }
  }
  > .dropdown-menu {
    visibility: hidden;
  }
`

interface MoreFiltersDropdownProps {
  filters: {
    id: string | string[]
    element: React.ReactNode
  }[]
}

export const MoreFiltersDropdown = ({
  filters,
}: MoreFiltersDropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  // One search over every filter in the menu, as in the dialogs' filters.
  const [searchQuery, setSearchQuery] = useState('')
  const location = useLocation()
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const searchParamsFallback = useMemo(() => {
    return queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
  }, [location])
  const searchParams = parsedQueryParams || searchParamsFallback

  const numSelections = useMemo(() => {
    return Object.keys(searchParams).reduce((acc, curr) => {
      if (
        !filters.some((filter) => {
          if (Array.isArray(filter.id)) {
            return filter.id.includes(curr)
          }
          return filter.id === curr
        })
      ) {
        return acc
      }
      if (Array.isArray(searchParams[curr])) {
        return acc + (searchParams[curr]?.length || 0)
      }
      return acc + 1
    }, 0)
  }, [searchParams, filters])

  const handleToggle = (show: boolean) => {
    setIsOpen(show)
    if (!show) setSearchQuery('')
  }

  const filtersElements = useMemo(
    () =>
      filters.map((filter) => (
        <div key={getFilterId(filter)} className="filter-item">
          {filter.element}
        </div>
      )),
    [filters],
  )

  return (
    <MoreFiltersTopMenu
      autoClose="outside"
      show={isOpen}
      onToggle={handleToggle}
    >
      <Dropdown.Toggle
        as={FilterDropdownToggle}
        id="more-filters-dropdown"
        $hasSelection={numSelections > 0}
        $isOverflow={true}
      >
        <FilterDropdownToggleContent>
          <p>More filters</p>
          {numSelections > 0 && (
            <FilterCountChip ml="xxs">{numSelections}</FilterCountChip>
          )}
        </FilterDropdownToggleContent>
      </Dropdown.Toggle>
      <Dropdown.Menu as={MoreFiltersMenu} rootCloseEvent="mousedown">
        <DropdownSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search filters..."
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
        <FilterMenuSearchContext.Provider value={searchQuery}>
          {filtersElements}
        </FilterMenuSearchContext.Provider>
      </Dropdown.Menu>
    </MoreFiltersTopMenu>
  )
}

const getFilterId = (filter: { id: string | string[] }): string => {
  if (Array.isArray(filter.id)) {
    return filter.id.join(',')
  }
  return filter.id
}
